/** Height in pixels of one hour in the grid. */
export const HOUR_HEIGHT = 64;

/** Default scroll position: show 07:00 at the top on mount. */
export const DEFAULT_SCROLL_HOUR = 7;

/** CSS Grid `fr` weight for weekday columns (Mon–Fri). */
export const WEEKDAY_FR = 1;

/** CSS Grid `fr` weight for weekend columns (Sat–Sun). */
export const WEEKEND_FR = 0.5;

/**
 * Build the CSS Grid column template for the day area.
 * Weekdays (indices 0–4 = Mon–Fri) get `WEEKDAY_FR fr`,
 * weekends (indices 5–6 = Sat–Sun) get `WEEKEND_FR fr`.
 * In day view (dayCount=1), always returns `1fr`.
 */
export function buildDayColumnsTemplate(dayCount: number): string {
  if (dayCount === 1) return '1fr';
  return Array.from({ length: dayCount }, (_, i) =>
    i >= 5 ? `${WEEKEND_FR}fr` : `${WEEKDAY_FR}fr`,
  ).join(' ');
}

/**
 * Cumulative column boundaries as fractions of total width [0, 1].
 * Used to map a pointer X position to the correct day index when
 * columns have unequal widths (weekday vs weekend).
 *
 * Returns an array of length `dayCount + 1` where `[0] = 0` and
 * `[dayCount] = 1`. The day at index `i` spans `[bounds[i], bounds[i+1])`.
 */
export function columnBoundaries(dayCount: number): number[] {
  if (dayCount === 1) return [0, 1];
  const weights = Array.from({ length: dayCount }, (_, i) =>
    i >= 5 ? WEEKEND_FR : WEEKDAY_FR,
  );
  const total = weights.reduce((s, w) => s + w, 0);
  const bounds = [0];
  for (let i = 0; i < dayCount; i++) {
    bounds.push(bounds[i] + weights[i] / total);
  }
  bounds[dayCount] = 1; // avoid floating-point drift
  return bounds;
}

/**
 * Map a pointer X position to a day index using weighted column
 * boundaries. More accurate than equal-division when weekday and
 * weekend columns have different widths.
 */
export function xToDayWeighted(x: number, gridBodyWidth: number, boundaries: number[]): number {
  const dayCount = boundaries.length - 1;
  const frac = x / gridBodyWidth;
  for (let i = 0; i < dayCount; i++) {
    if (frac < boundaries[i + 1]) return i;
  }
  return dayCount - 1;
}

/** Snap any minute value to the nearest 15-minute increment. */
export function snapToGrid(minutes: number): number {
  return Math.round(minutes / 15) * 15;
}

/** Convert a Date to minutes-since-midnight (local time). */
export function dateToMinutes(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

/** Convert minutes-since-midnight to a pixel Y offset. */
export function minutesToY(minutes: number): number {
  return (minutes / 60) * HOUR_HEIGHT;
}

/** Convert a pixel Y offset back to minutes. */
export function yToMinutes(y: number): number {
  return (y / HOUR_HEIGHT) * 60;
}

/**
 * Position + size of one entry block.
 */
export interface EntryPosition {
  top: number; // px from grid top
  height: number; // px
  left: number; // fraction 0–1 within the day column
  width: number; // fraction 0–1
}

/**
 * Compute block position from a time entry's start/end.
 * startMinutes/endMinutes are minutes-since-midnight.
 * overlapIndex and overlapCount come from resolveOverlaps.
 */
export function entryToPosition(
  startMinutes: number,
  endMinutes: number,
  overlapIndex: number,
  overlapCount: number,
): EntryPosition {
  const top = minutesToY(startMinutes);
  const height = Math.max(minutesToY(endMinutes - startMinutes), minutesToY(15)); // min 15min
  const width = 1 / overlapCount;
  const left = overlapIndex * width;
  return { top, height, left, width };
}

/**
 * An entry with its overlap layout info.
 */
export interface OverlapEntry {
  id: string;
  startMinutes: number;
  endMinutes: number;
  overlapIndex: number;
  overlapCount: number;
}

/**
 * Resolve overlapping entries within a single day.
 * Input: array of { id, startMinutes, endMinutes }.
 * Output: same entries augmented with overlapIndex and overlapCount.
 *
 * Algorithm:
 * 1. Sort by startMinutes, then by endMinutes desc (longer entries first)
 * 2. Find conflict groups (entries whose time ranges overlap)
 * 3. Within each group, assign sequential indices and set count = group size
 */
export function resolveOverlaps(
  entries: Array<{ id: string; startMinutes: number; endMinutes: number }>,
): OverlapEntry[] {
  if (entries.length === 0) return [];

  const sorted = [...entries].sort((a, b) => {
    if (a.startMinutes !== b.startMinutes) return a.startMinutes - b.startMinutes;
    return b.endMinutes - a.endMinutes; // longer entries first
  });

  // Find connected components (conflict groups)
  const groups: Array<typeof sorted> = [];
  let currentGroup = [sorted[0]];
  let groupEnd = sorted[0].endMinutes;

  for (let i = 1; i < sorted.length; i++) {
    const entry = sorted[i];
    if (entry.startMinutes < groupEnd) {
      // Overlaps with current group
      currentGroup.push(entry);
      groupEnd = Math.max(groupEnd, entry.endMinutes);
    } else {
      // New group
      groups.push(currentGroup);
      currentGroup = [entry];
      groupEnd = entry.endMinutes;
    }
  }
  groups.push(currentGroup);

  // Assign indices within each group
  const result: OverlapEntry[] = [];
  for (const group of groups) {
    const count = group.length;
    group.forEach((entry, index) => {
      result.push({
        ...entry,
        overlapIndex: index,
        overlapCount: count,
      });
    });
  }

  return result;
}

/**
 * Format minutes-since-midnight to "HH:MM".
 */
export function formatTimeFromMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
